import { Map, List, fromJS } from 'immutable';
import Actions from 'flow/actions';

import {
    getApplicationLanguage,
    getSpokenLanguage,
} from 'flow/utils/browser-utils';
import { GeoLocationStatus } from 'flow/utils/geolocation-utils';
import {
    hasAcceptedCookies,
    hasRejectedCookies,
    hasPartialCookieConsent,
    getConsentCategories,
    setAcceptedCookies,
    setRejectedCookies,
    setPartialCookieConsent,
    setConsentCategories,
} from 'flow/utils/cookie-utils';
import { keyIn, listToMap } from 'flow/utils';

const allCategoriesAccepted = fromJS({
    analytics: true,
    errorMonitoring: true,
});

const getInitialAnalyticsConsent = () => {
    if (hasAcceptedCookies()) return 'accepted';
    if (hasRejectedCookies()) return 'rejected';
    if (hasPartialCookieConsent()) return 'partial';
    return null;
};

const deriveConsentState = (consent) => ({
    analyticsConsent: consent,
    consentCategories:
        consent === 'accepted'
            ? allCategoriesAccepted
            : consent === 'partial'
              ? fromJS(getConsentCategories())
              : null,
});

const initialAnalyticsConsent = getInitialAnalyticsConsent();
const { consentCategories: initialConsentCategories } = deriveConsentState(
    initialAnalyticsConsent,
);

const initialAppState = Map({
    searchText: '',
    state: 'boot',
    error: undefined,
    layoutColumns: 0,
    extension: Map() /* logged in ExtensionEntityKey */,
    visible: true,
    focus: true,
    topConnection: null,
    notifications: Map(),
    applicationLanguage: getApplicationLanguage(),
    spokenLanguage: getSpokenLanguage(),
    allowedVersion: true,
    missedCallCount: 0,
    snackbarList: List(),
    i18next: undefined,
    appStartTime: Date.now(),
    softphoneInComCanvas: false,
    // Replaced by this client's stored choice at startup (loadMakeCallsEnabled).
    makeCallsEnabled: true,
    audioInitiated: undefined,
    isRoaming: false,
    geoLocationStatus: GeoLocationStatus.PENDING,
    geoLocation: null,
    geoLocationTimestamp: null,
    geoOutsideFence: false,
    analyticsConsent: initialAnalyticsConsent,
    consentCategories: initialConsentCategories,
    autoLogin: Map(),
    loggingOut: false,
});

// eslint-disable-next-line complexity
export const appReducer = (state = initialAppState, action) => {
    switch (action ? action.type : null) {
        case Actions.CLEAR_STATE: {
            const topConnection = state.get('topConnection');

            if (topConnection) {
                topConnection.close();
            }

            if (state.has('topRetryTimeoutId')) {
                clearTimeout(state.get('topRetryTimeoutId'));
            }

            // loginData is login status, not per-session app data, so CLEAR_STATE
            // keeps it; it's cleared explicitly on logout via clearLoginData.
            // (Wiping it here flipped isSuccessLogin and remounted Main into a
            // second boot during the mid-boot new-user data wipe.)
            return initialAppState
                .merge(
                    state.filter(
                        keyIn(
                            'electronArgs',
                            'embedded',
                            'layoutColumns',
                            'autoLogin',
                            'loginData',
                        ),
                    ),
                )
                .merge(deriveConsentState(getInitialAnalyticsConsent()));
        }
        case Actions.APP_STATE:
            return state
                .update('state', () => action.state)
                .update('error', (prev) =>
                    action.error
                        ? prev
                            ? prev + ', ' + action.error
                            : action.error
                        : prev,
                );

        case Actions.APP_ALLOWED_VERSION: {
            return state.set('allowedVersion', fromJS(action.data));
        }
        case Actions.APP_COMPANION_ENABLED: {
            // Server-owned feature switch. Absent or false means every companion code path stays
            // dormant and the client behaves exactly as it did before the companion work.
            return state.set('companionEnabled', action.enabled === true);
        }
        case Actions.APP_COMPANION_LOCATION_STATUS: {
            // Pushed by the companion, never stored server-side, so it is only ever as current as
            // the last report - and absent until the first one arrives. Surfaces treat absent as
            // "no reason to complain" rather than blocked, so a companion that never reports
            // behaves as it did before this existed.
            return state
                .set('companionLocationStatus', action.locationStatus ?? null)
                .set(
                    'companionLocationStatusDeviceId',
                    action.deviceId ?? null,
                );
        }
        case Actions.APP_COMPANION_LOCATION_REPORT: {
            // What this machine knows about its own location access, and what it has told the
            // account's surfaces. Both belong here rather than in module state so that "have we
            // already said this?" has one answer, and so a logout clears them with everything else.
            //
            // reportedStatus is cleared, not set, when a report fails or when a reconnect means
            // the surfaces may have missed the last one - so the next attempt goes out again.
            return state
                .set('companionKnownLocationStatus', action.knownStatus)
                .set('companionReportedLocationStatus', action.reportedStatus)
                .set('companionLocationReportedAt', action.reportedAt);
        }
        case Actions.APP_HANDOVER_TO_DESKTOP: {
            // Per surface: whether this Teams tab or browser hands its calls to the desktop app.
            // The mirror of useAsCompanion below - one end offers the calls, the other takes them -
            // and deliberately not a call method: the user's softphone, IP phone and mobile choices
            // stay meaningful, because they are what happens when no desktop app is there.
            return state.set('handoverToDesktop', action.enabled === true);
        }
        case Actions.APP_USE_AS_COMPANION: {
            // User-owned, per install: whether *this* desktop app should act as the companion.
            // Distinct from companionEnabled, which is the backend feature switch for the user and
            // is true in every client they log into. Both have to be true for this app to be the
            // companion - the switch says the feature exists, this says the user picked this machine.
            return state.set('useAsCompanion', action.enabled === true);
        }
        case Actions.APP_COMPANION_HEARTBEAT_ENABLED: {
            return state.set(
                'companionHeartbeatEnabled',
                action.enabled === true,
            );
        }
        case Actions.APP_COMPANION_RESPONSIVE: {
            return state.set('companionResponsive', action.responsive);
        }
        case Actions.APP_COMPANION_DEVICE_NAME: {
            return state.set('companionDeviceName', action.name || null);
        }
        case Actions.APP_COMPANION_ROLE_LOST: {
            // Held only so the banner has something to render. Cleared on dismiss, and when
            // the role comes back.
            return state.set('companionRoleLost', action.lost === true);
        }
        case Actions.RESULT_COMPANION_DEVICES: {
            return state.set('companionDevices', fromJS(action.devices || []));
        }
        case Actions.APP_LOGIN_DATA: {
            return state.set('loginData', fromJS(action.data));
        }
        case Actions.APP_LOGIN_DATA_CLEAR: {
            return state.delete('loginData');
        }
        case Actions.APP_LOG_OUT: {
            return state.set('loggingOut', action.data);
        }
        case Actions.APP_EXTERNAL_SIGNIN_DATA: {
            return state.set('externalSigninData', fromJS(action.data));
        }
        case Actions.APP_EMBEDDED: {
            return state.set('embedded', action.data);
        }
        case Actions.SET_PBX_PAGE_MOUNTED: {
            return state.setIn(['pbxPageMounted', action.queueKey], true);
        }
        case Actions.SET_PBX_PAGE_UNMOUNTED: {
            return state.deleteIn(['pbxPageMounted', action.queueKey]);
        }
        case Actions.SET_PBX_SECTION_MOUNTED: {
            return state.set('pbxSectionMounted', action.isMounted);
        }
        case Actions.SET_MISSED_CALLS_WIDGET_IS_MOUNTED: {
            if (action.isMounted) {
                return state.setIn(
                    ['componentsMounted', 'missedCallsWidget'],
                    true,
                );
            } else {
                return state.deleteIn([
                    'componentsMounted',
                    'missedCallsWidget',
                ]);
            }
        }
        case Actions.SET_MOUNTED_CHAT_SESSION: {
            if (action.chatSessionKey) {
                return state.set(
                    'mountedChatSessionKey',
                    action.chatSessionKey,
                );
            }
            return state.delete('mountedChatSessionKey');
        }
        case Actions.REGISTER_TOP_CONNECTION:
            return state.set('topConnection', action.data);
        case Actions.UNREGISTER_TOP_CONNECTION: {
            const topConnection = state.get('topConnection');

            if (topConnection) {
                // Supply CloseEvent.code '1000', indicating normal closure
                topConnection.close(1000);
            }

            return state.set(
                'topConnection',
                initialAppState.get('topConnection'),
            );
        }
        case Actions.RESULT_SESSION: {
            return state.update('extension', () => fromJS(action.data));
        }
        case Actions.RESULT_NEW_PROFILE:
            return state.update('extension', (prev) =>
                prev
                    .deleteIn(['activeProfile'])
                    .mergeIn(['activeProfile'], fromJS(action.data)),
            );
        case Actions.SEARCH:
            return state.set('searchText', action.searchText);
        case Actions.ACCEPTED_COOKIES: {
            setAcceptedCookies();
            return state.merge({
                analyticsConsent: 'accepted',
                consentCategories: allCategoriesAccepted,
            });
        }
        case Actions.REJECTED_COOKIES: {
            setRejectedCookies();
            return state.merge({
                analyticsConsent: 'rejected',
                consentCategories: null,
            });
        }
        case Actions.SET_COOKIE_CONSENT_CATEGORIES: {
            setPartialCookieConsent();
            setConsentCategories(action.categories);
            return state.merge({
                analyticsConsent: 'partial',
                consentCategories: Map(action.categories),
            });
        }
        case Actions.LAYOUT_COLUMNS:
            return state.set('layoutColumns', action.data);
        case Actions.APP_VISIBLE:
            return state.update('visible', () => action.data);
        case Actions.APP_FOCUS:
            return state.update('focus', () => action.data);
        case Actions.NETWORK_CONNECTION_CHANGE: {
            return state.setIn(
                ['networkConnection', 'established'],
                action.data,
            );
        }
        case Actions.ADD_NOTICE:
            return state.setIn(
                ['notifications', action.key],
                action.notification,
            );
        case Actions.DELETE_NOTICE:
            return state.deleteIn(['notifications', action.key]);
        case Actions.APP_LANGUAGE:
            return state.set('applicationLanguage', action.data);
        case Actions.SPOKEN_LANGUAGE:
            return state.set('spokenLanguage', action.data);
        case Actions.ELECTRON_ARGS:
            return state.set('electronArgs', fromJS(action.args));
        case Actions.TEMPORARY_PASSWORD_STATUS:
            return state.set('temporaryPasswordState', fromJS(action.data));
        case Actions.CALL_METHOD:
            return state.set('callMethod', action.callMethod);
        case Actions.CALL_CANVAS_SIZE:
            return state.set('callCanvasSize', action.size);
        case Actions.AUTOANSWER_IP_PHONE:
            return state.set('autoAnswerWithIpPhone', action.autoAnswer);
        case Actions.MAKE_CALLS_ENABLED:
            return state.set('makeCallsEnabled', action.makeCallsEnabled);
        case Actions.RESULT_OPERATOR_USERS: {
            return state.set('operatorUsers', fromJS(action.data));
        }
        case Actions.SHOW_CALL_DIALOG:
            return state
                .setIn(['callDialog', 'number'], action.number)
                .setIn(
                    ['callDialog', 'extensionToCall'],
                    action.extensionToCall,
                )
                .setIn(
                    ['callDialog', 'extraCallOptions'],
                    action.extraCallOptions,
                )
                .setIn(['callDialog', 'dialogType'], action.dialogType);
        case Actions.SET_CALL_DIALOG_TYPE:
            return state.setIn(['callDialog', 'dialogType'], action.dialogType);
        case Actions.HIDE_CALL_DIALOG:
            return state.delete('callDialog');
        case Actions.TOP_RETRY_TIMEOUT_ID:
            return state.set('topRetryTimeoutId', action.timeoutId);
        case Actions.ADD_MISSEDCALL_STATE:
            return state.update('missedCallCount', (prev) => ++prev);
        case Actions.CLEAR_MISSEDCALL_STATE:
            return state.set('missedCallCount', 0);
        case Actions.MIC_PERMISSION: {
            return state.set('micPermission', fromJS(action.data));
        }
        case Actions.GEO_LOCATION: {
            return state
                .set(
                    'geoLocationStatus',
                    action.coordinates
                        ? GeoLocationStatus.AVAILABLE
                        : GeoLocationStatus.BLOCKED,
                )
                .set('geoLocation', fromJS(action.coordinates))
                .set('geoLocationPermissionDenied', !!action.permissionDenied)
                .set('geoLocationTimestamp', action.timestamp);
        }
        case Actions.GEO_OUTSIDE_FENCE: {
            return state.set('geoOutsideFence', action.outsideFence);
        }
        case Actions.PHONE_SESSION_ACCEPTED: {
            return state.set('geoOutsideFence', false);
        }
        case Actions.GEO_RESTRICTION_ENABLED: {
            return state.set(
                'geoRestrictionEnabled',
                typeof action.enabled === 'boolean' ? action.enabled : null,
            );
        }
        case Actions.ADD_SNACKBAR: {
            return state.update('snackbarList', (list) => {
                const index = list.findIndex(
                    (item) => item.get('key') === action.key,
                );
                const entry = Map({
                    message: action.message,
                    i18nextMessage: action.i18nextMessage,
                    i18nextMessageProps: action.i18nextMessageProps,
                    hasCloseButton: !!action.hasCloseButton,
                    key:
                        action.key !== undefined
                            ? action.key
                            : new Date().getTime().toString(),
                });
                return index === -1 ? list.push(entry) : list.set(index, entry);
            });
        }
        case Actions.REMOVE_SNACKBAR: {
            return state.update('snackbarList', (list) => list.shift());
        }
        case Actions.SET_AUDIO_INITIATED: {
            return state.set('audioInitiated', action.audioInitiated);
        }
        case Actions.SET_LOGGED_IN_USER_HAS_OMNICHANNEL_FEATURE: {
            return state.setIn(
                ['extension', 'hasOmnichannelFeature'],
                action.hasFeature,
            );
        }
        case Actions.SET_LOGGED_IN_USER_HAS_CTI_INTEGRATION_FEATURE: {
            return state.setIn(
                ['extension', 'hasCtiIntegrationFeature'],
                action.hasFeature,
            );
        }
        case Actions.SET_LOGGED_IN_USER_HAS_ZENDESK_CALL_LOGGING_APP: {
            return state.setIn(
                ['extension', 'hasZendeskCallLoggingApp'],
                action.hasFeature,
            );
        }
        case Actions.SET_LOGGED_IN_USER_HAS_SUPERVISOR_FEATURE: {
            return state.setIn(
                ['extension', 'hasSupervisorFeature'],
                action.hasFeature,
            );
        }
        case Actions.SET_LOGGED_IN_USER_HAS_RETURN_BLIND_TRANSFER_FEATURE: {
            return state.setIn(
                ['extension', 'hasReturnBlindTransferFeature'],
                action.hasFeature,
            );
        }
        case Actions.RESULT_USER_CAPABILITIES: {
            const capabilitiesNamesMap = listToMap(
                fromJS(action.capabilities || []),
                (capability) => capability.get('name'),
            );
            return state.setIn(
                ['extension', 'capabilities'],
                capabilitiesNamesMap,
            );
        }
        case Actions.IS_ROAMING: {
            return state.set('isRoaming', action.isRoaming);
        }
        case Actions.AUTO_LOGIN_WITH_ACCESS_TOKEN: {
            return state.setIn(
                ['autoLogin', 'autoLoginAccessTokenId'],
                action.autoLoginAccessTokenId,
            );
        }
        default:
            return state;
    }
};

export default appReducer;
